// ============================================================================
// TEMPLATE CATALOG PAGE
// Browse and manage session templates - table view with Tailwind styling
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit2, Trash2, Copy, Send, Eye, ChevronUp, ChevronDown, Layers, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  SessionTemplate,
  TemplateScope,
  TemplateType,
  TemplatePublishingStatus
} from '../types';
import {
  browseSessionTemplates,
  deleteSessionTemplate,
  submitTemplateForReview
} from '../services/templateService';
import { EmptyState } from '../components/EmptyState';

type SortField = 'name' | 'category' | 'scope' | 'status' | 'phases' | 'usage' | 'version';
type SortDirection = 'asc' | 'desc';

export default function TemplateCatalog() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [templates, setTemplates] = useState<SessionTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedScope, setSelectedScope] = useState<TemplateScope | ''>('');
  const [selectedCategory, setSelectedCategory] = useState<TemplateType | ''>('');
  const [showPrebuiltOnly, setShowPrebuiltOnly] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let allTemplates: SessionTemplate[] = [];

      if (!selectedScope && !selectedCategory && !searchTerm && !showPrebuiltOnly && user?.userId) {
        const [userTemplates, orgTemplates, sessionTemplates, prebuiltTemplates] = await Promise.all([
          browseSessionTemplates({ scope: TemplateScope.USER, ownerId: user.userId }).catch(() => ({ templates: [] })),
          browseSessionTemplates({ scope: TemplateScope.ORGANIZATION, ownerId: user.userId }).catch(() => ({ templates: [] })),
          browseSessionTemplates({ scope: TemplateScope.SESSION }).catch(() => ({ templates: [] })),
          browseSessionTemplates({ isPrebuilt: true }).catch(() => ({ templates: [] }))
        ]);

        const combined = [
          ...userTemplates.templates,
          ...orgTemplates.templates,
          ...sessionTemplates.templates,
          ...prebuiltTemplates.templates
        ];

        const uniqueMap = new Map<string, SessionTemplate>();
        combined.forEach(template => {
          const key = `${template.sessionTemplateId}-${template.versionNumber}`;
          if (!uniqueMap.has(key)) {
            uniqueMap.set(key, template);
          }
        });

        allTemplates = Array.from(uniqueMap.values());
      } else {
        const params: any = {
          scope: selectedScope || undefined,
          category: selectedCategory || undefined,
          isPrebuilt: showPrebuiltOnly || undefined,
          search: searchTerm || undefined
        };

        if ((selectedScope === TemplateScope.USER || selectedScope === TemplateScope.ORGANIZATION) && user?.userId) {
          params.ownerId = user.userId;
        }

        const result = await browseSessionTemplates(params);
        allTemplates = result.templates;
      }

      setTemplates(allTemplates);
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [selectedScope, selectedCategory, searchTerm, showPrebuiltOnly, user]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSearch = () => fetchTemplates();

  const handleDelete = async (sessionTemplateId: string, versionNumber: number) => {
    if (!window.confirm('Are you sure you want to delete this template? This cannot be undone.')) return;
    try {
      await deleteSessionTemplate(sessionTemplateId, versionNumber);
      fetchTemplates();
    } catch (err: any) {
      alert(`Failed to delete template: ${err.message}`);
    }
  };

  const handleClone = async (template: SessionTemplate) => {
    const newName = window.prompt(`Clone "${template.name}" as:`, `${template.name} (Copy)`);
    if (!newName) return;
    navigate('/templates/create', { state: { cloneFrom: template, name: newName } });
  };

  const handlePublish = async (template: SessionTemplate) => {
    const notes = window.prompt(`Submit "${template.name}" for review. Add notes (optional):`);
    if (notes === null) return;
    try {
      await submitTemplateForReview('SESSION', template.sessionTemplateId, template.versionNumber, 'ORGANIZATION', notes || undefined);
      alert('Template submitted for review!');
      fetchTemplates();
    } catch (err: any) {
      alert(`Failed to submit for review: ${err.message}`);
    }
  };

  const getCategoryClasses = (category: TemplateType): string => {
    const map: Record<TemplateType, string> = {
      [TemplateType.WORKSHOP]: 'bg-indigo-100 text-indigo-700',
      [TemplateType.BRAINSTORM]: 'bg-pink-100 text-pink-700',
      [TemplateType.REVIEW]: 'bg-blue-100 text-blue-700',
      [TemplateType.PLANNING]: 'bg-emerald-100 text-emerald-700',
      [TemplateType.CUSTOM]: 'bg-violet-100 text-violet-700'
    };
    return map[category] || 'bg-slate-100 text-slate-700';
  };

  const getStatusClasses = (status: TemplatePublishingStatus): string => {
    const map: Record<TemplatePublishingStatus, string> = {
      [TemplatePublishingStatus.DRAFT]: 'bg-slate-100 text-slate-600',
      [TemplatePublishingStatus.PENDING_REVIEW]: 'bg-amber-100 text-amber-700',
      [TemplatePublishingStatus.APPROVED]: 'bg-green-100 text-green-700',
      [TemplatePublishingStatus.REJECTED]: 'bg-red-100 text-red-700'
    };
    return map[status];
  };

  const filteredTemplates = templates.filter(template => {
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      return template.name.toLowerCase().includes(query) || template.description?.toLowerCase().includes(query);
    }
    return true;
  });

  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    let aValue: any, bValue: any;
    switch (sortField) {
      case 'name': aValue = a.name.toLowerCase(); bValue = b.name.toLowerCase(); break;
      case 'category': aValue = a.category; bValue = b.category; break;
      case 'scope': aValue = a.scope; bValue = b.scope; break;
      case 'status': aValue = a.publishingStatus; bValue = b.publishingStatus; break;
      case 'phases': aValue = a.phaseReferences?.length || 0; bValue = b.phaseReferences?.length || 0; break;
      case 'usage': aValue = a.usageCount || 0; bValue = b.usageCount || 0; break;
      case 'version': aValue = a.versionNumber; bValue = b.versionNumber; break;
      default: aValue = a.name; bValue = b.name;
    }
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode }> = ({ field, children }) => (
    <th
      onClick={() => handleSort(field)}
      className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400 cursor-pointer select-none hover:text-[#052831] transition-colors"
    >
      <div className="flex items-center gap-1.5">
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  );

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

      {/* Header */}
      <header className="flex items-center justify-between border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <FileText size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Session Templates</h1>
            <p className="text-slate-500 text-sm font-medium">Browse, create, and manage workshop session templates</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/templates/phases')}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
          >
            <Layers size={16} />
            Phase Library
          </button>
          <button
            onClick={() => navigate('/templates/create')}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
          >
            <Plus size={16} />
            Create Template
          </button>
        </div>
      </header>

      {/* Filters + Table — one unit */}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-[#e2e8f0]">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[280px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-[#e2e8f0] bg-white text-[#052831] focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400 text-sm"
              />
            </div>
            <select
              value={selectedScope}
              onChange={(e) => setSelectedScope(e.target.value as TemplateScope | '')}
              className="px-3 py-2.5 rounded-xl border border-[#e2e8f0] text-sm text-[#052831] focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer"
            >
              <option value="">All Scopes</option>
              <option value={TemplateScope.SESSION}>Session</option>
              <option value={TemplateScope.USER}>User</option>
              <option value={TemplateScope.ORGANIZATION}>Organization</option>
            </select>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as TemplateType | '')}
              className="px-3 py-2.5 rounded-xl border border-[#e2e8f0] text-sm text-[#052831] focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer"
            >
              <option value="">All Categories</option>
              <option value={TemplateType.WORKSHOP}>Workshop</option>
              <option value={TemplateType.BRAINSTORM}>Brainstorm</option>
              <option value={TemplateType.REVIEW}>Review</option>
              <option value={TemplateType.PLANNING}>Planning</option>
              <option value={TemplateType.CUSTOM}>Custom</option>
            </select>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-[#052831]">
              <input
                type="checkbox"
                checked={showPrebuiltOnly}
                onChange={(e) => setShowPrebuiltOnly(e.target.checked)}
                className="rounded border-slate-300 text-primary focus:ring-primary"
              />
              Prebuilt only
            </label>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20 text-[#64748b]">
            <div className="text-sm font-medium">Loading templates...</div>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-sm text-red-700">Failed to load templates: {error}</p>
          </div>
        ) : sortedTemplates.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={searchTerm || selectedScope || selectedCategory ? 'No templates found' : 'No templates yet'}
            description={searchTerm || selectedScope || selectedCategory ? 'Try adjusting your filters' : 'Create your first template to get started'}
            action={
              <button
                onClick={() => searchTerm || selectedScope || selectedCategory
                  ? (() => { setSearchTerm(''); setSelectedScope(''); setSelectedCategory(''); })()
                  : navigate('/templates/create')
                }
                className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-[#052831] font-bold rounded-xl transition-all shadow-md shadow-primary/20 text-sm"
              >
                {searchTerm || selectedScope || selectedCategory ? 'Clear Filters' : 'Create Template'}
              </button>
            }
          />
      ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                  <SortableHeader field="name">Name</SortableHeader>
                  <SortableHeader field="category">Category</SortableHeader>
                  <SortableHeader field="scope">Scope</SortableHeader>
                  <SortableHeader field="status">Status</SortableHeader>
                  <SortableHeader field="phases">Phases</SortableHeader>
                  <SortableHeader field="usage">Usage</SortableHeader>
                  <SortableHeader field="version">Version</SortableHeader>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {sortedTemplates.map((template) => (
                  <tr
                    key={`${template.sessionTemplateId}-${template.versionNumber}`}
                    onClick={() => navigate(`/templates/${template.sessionTemplateId}/preview`)}
                    className="cursor-pointer hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="px-4 py-4 max-w-[300px]">
                      <div className="font-medium text-[#052831]">
                        {template.name}
                        {template.isDefault && (
                          <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold uppercase">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#64748b] truncate mt-0.5">{template.description}</div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${getCategoryClasses(template.category)}`}>
                        {template.category}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs text-[#64748b]">{template.scope}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${getStatusClasses(template.publishingStatus)}`}>
                        {template.publishingStatus}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs text-[#64748b]">{template.phaseReferences.length}</td>
                    <td className="px-4 py-4 text-xs text-[#64748b]">{template.usageCount || 0}</td>
                    <td className="px-4 py-4 text-xs text-slate-400">v{template.versionNumber}</td>
                    <td className="px-4 py-4">
                      <div className="flex gap-1.5 justify-end" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => navigate(`/templates/${template.sessionTemplateId}/preview`)}
                          className="p-1.5 rounded-lg border border-[#e2e8f0] text-[#64748b] hover:text-primary hover:border-primary/30 transition-all"
                          title="Preview"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={() => navigate(`/templates/${template.sessionTemplateId}/edit`)}
                          className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-all"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleClone(template)}
                          className="p-1.5 rounded-lg border border-[#e2e8f0] text-[#64748b] hover:text-violet-600 hover:border-violet-300 transition-all"
                          title="Clone"
                        >
                          <Copy size={14} />
                        </button>
                        {template.publishingStatus === TemplatePublishingStatus.DRAFT && template.scope === TemplateScope.USER && (
                          <button
                            onClick={() => handlePublish(template)}
                            className="p-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-all"
                            title="Submit for Review"
                          >
                            <Send size={14} />
                          </button>
                        )}
                        {template.publishingStatus === TemplatePublishingStatus.DRAFT && (
                          <button
                            onClick={() => handleDelete(template.sessionTemplateId, template.versionNumber)}
                            className="p-1.5 rounded-lg border border-red-200 text-red-400 hover:text-red-600 hover:border-red-300 transition-all"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      )}

      </div>
      </div>
    </div>
  );
}
